import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { flushSync } from 'react-dom';
import { LoadingOverlay } from '../components/ui/LoadingOverlay';
import { readJson, writeJson } from '../lib/persist';
import { APP_NAV, useAppNavigation } from './AppNavigationContext';

const CURRENCY_LOADING_MESSAGES = ['Getting current rate'];
const SOURCE_CURRENCY = 'DKK';
const PAGE_DASHBOARD_STORAGE_KEY = 'vi.pageDashboard.v1';

export const FILTER_PAGE_IDS = [
  APP_NAV.OVERVIEW,
  APP_NAV.TRENDS,
  APP_NAV.PRODUCTS,
  APP_NAV.EVENTS,
  APP_NAV.VENUES,
];

export const DEFAULT_COMMITTED_FILTERS = {
  timePeriod: 'all_time',
  start: null,
  end: null,
  venue: 'all',
  event: 'all',
  productType: 'all',
  queryString: '',
};

function createDefaultPageState() {
  return {
    filters: { ...DEFAULT_COMMITTED_FILTERS },
    displayCurrency: SOURCE_CURRENCY,
    currencyConverting: false,
  };
}

function createInitialPageState() {
  return FILTER_PAGE_IDS.reduce((acc, pageId) => {
    acc[pageId] = createDefaultPageState();
    return acc;
  }, {});
}

function hydratePageState() {
  const defaults = createInitialPageState();
  const stored = readJson(sessionStorage, PAGE_DASHBOARD_STORAGE_KEY, null);
  if (!stored || typeof stored !== 'object') return defaults;

  FILTER_PAGE_IDS.forEach((pageId) => {
    const saved = stored[pageId];
    if (!saved || typeof saved !== 'object') return;
    defaults[pageId] = {
      ...createDefaultPageState(),
      filters: {
        ...DEFAULT_COMMITTED_FILTERS,
        ...(saved.filters || {}),
      },
      displayCurrency: saved.displayCurrency || SOURCE_CURRENCY,
      currencyConverting: false,
    };
  });
  return defaults;
}

function persistPageState(pageState) {
  const slim = FILTER_PAGE_IDS.reduce((acc, pageId) => {
    const entry = pageState[pageId] || createDefaultPageState();
    acc[pageId] = {
      filters: entry.filters,
      displayCurrency: entry.displayCurrency,
    };
    return acc;
  }, {});
  writeJson(sessionStorage, PAGE_DASHBOARD_STORAGE_KEY, slim);
}

const PageDashboardContext = createContext(null);

export function PageDashboardProvider({ children }) {
  const { activeNav } = useAppNavigation();
  const [pageState, setPageState] = useState(hydratePageState);

  useEffect(() => {
    persistPageState(pageState);
  }, [pageState]);

  const setPageFilters = useCallback((pageId, filters) => {
    if (!FILTER_PAGE_IDS.includes(pageId)) return;

    setPageState((prev) => ({
      ...prev,
      [pageId]: {
        ...prev[pageId],
        filters: {
          ...prev[pageId].filters,
          ...filters,
        },
      },
    }));
  }, []);

  const setPageDisplayCurrency = useCallback((pageId, currency) => {
    if (!FILTER_PAGE_IDS.includes(pageId)) return;

    flushSync(() => {
      setPageState((prev) => {
        if (prev[pageId]?.displayCurrency === currency) {
          return prev;
        }

        return {
          ...prev,
          [pageId]: {
            ...prev[pageId],
            displayCurrency: currency,
            currencyConverting: currency !== SOURCE_CURRENCY,
          },
        };
      });
    });
  }, []);

  const setPageCurrencyConverting = useCallback((pageId, converting) => {
    if (!FILTER_PAGE_IDS.includes(pageId)) return;

    setPageState((prev) => ({
      ...prev,
      [pageId]: {
        ...prev[pageId],
        currencyConverting: converting,
      },
    }));
  }, []);

  const value = useMemo(
    () => ({
      pageState,
      setPageFilters,
      setPageDisplayCurrency,
      setPageCurrencyConverting,
      sourceCurrency: SOURCE_CURRENCY,
    }),
    [pageState, setPageFilters, setPageDisplayCurrency, setPageCurrencyConverting]
  );

  const activeConverting = FILTER_PAGE_IDS.includes(activeNav)
    ? pageState[activeNav]?.currencyConverting
    : false;

  return (
    <PageDashboardContext.Provider value={value}>
      <LoadingOverlay loading={Boolean(activeConverting)} messages={CURRENCY_LOADING_MESSAGES} />
      {children}
    </PageDashboardContext.Provider>
  );
}

export function usePageDashboard() {
  const context = useContext(PageDashboardContext);
  if (!context) {
    throw new Error('usePageDashboard must be used within PageDashboardProvider');
  }
  return context;
}

export function usePageSettings(pageId) {
  const { pageState, setPageFilters, setPageDisplayCurrency, setPageCurrencyConverting, sourceCurrency } =
    usePageDashboard();

  const settings = pageState[pageId] || createDefaultPageState();

  const updateFilters = useCallback(
    (filters) => {
      setPageFilters(pageId, filters);
    },
    [pageId, setPageFilters]
  );

  const updateDisplayCurrency = useCallback(
    (currency) => {
      setPageDisplayCurrency(pageId, currency);
    },
    [pageId, setPageDisplayCurrency]
  );

  const updateCurrencyConverting = useCallback(
    (converting) => {
      setPageCurrencyConverting(pageId, converting);
    },
    [pageId, setPageCurrencyConverting]
  );

  return {
    committedFilters: settings.filters,
    displayCurrency: settings.displayCurrency,
    currencyConverting: settings.currencyConverting,
    setCommittedFilters: updateFilters,
    setDisplayCurrency: updateDisplayCurrency,
    setCurrencyConverting: updateCurrencyConverting,
    sourceCurrency,
  };
}
